/**
 * Base class for workflow services that provides a standard loading pattern.
 *
 * WorkflowService.load(caseId) is the single entry point for data loading:
 * - Internally uses DataLoaderService for data fetching
 * - Emits aggregate progress events for simple UI consumption
 * - Transforms raw data to domain objects after loading completes
 *
 * Subclasses implement:
 * - getDataSlots(): Return slot keys needed for this workflow
 * - transformLoadedData(): Transform raw data to domain objects
 */

import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, IPubSubService } from '../core/types';
import { DisplayFeatureManager } from '../display/display-feature-manager';
import type { AllCompletePayload, SlotLoadedPayload } from '../services/data-loader-service';
import { DataLoaderService } from '../services/data-loader-service';

// ============================================================================
// Types
// ============================================================================

/**
 * Progress payload for aggregate loading progress events.
 */
export interface LoadProgressPayload {
    /** Number of slots that have completed loading (success, error, or not_found) */
    loaded: number;
    /** Total number of slots to load */
    total: number;
    /** Whether all slots have finished loading */
    isComplete: boolean;
}

/**
 * Subset of case data stored on the workflow service for UI consumers.
 * Avoids coupling to the full ICaseResponse and prevents React-context refetch cascades.
 */
export interface CaseMetadata {
    id: string;
    name: string;
    firstname: string | null;
    lastname: string | null;
    dob: string | null;
    gender: string | null;
    createdAt: string;
}

/**
 * Error payload for transform errors.
 */
export interface TransformErrorPayload {
    /** Error message */
    message: string;
    /** Original error if available */
    error?: unknown;
}

/**
 * Interface that all workflow services must implement.
 */
export interface IWorkflowService extends IPubSubService {
    /** Unique identifier for this workflow type */
    readonly WORKFLOW_ID: string;

    /**
     * Load all data for a case.
     * This is the single entry point for data loading.
     * @param caseId - The case ID to load data for
     */
    load(caseId: string, signal?: AbortSignal): Promise<void>;

    /**
     * Check if the workflow is ready for use.
     * @returns true if minimum required data is loaded
     */
    isReady(): boolean;

    /**
     * Get current loading progress.
     * @returns Progress information
     */
    getProgress(): LoadProgressPayload;

    /**
     * Get case metadata stored on this service.
     */
    getCaseMetadata(): CaseMetadata | null;

    /**
     * Store case metadata and broadcast the change.
     */
    setCaseMetadata(metadata: CaseMetadata): void;
}

// ============================================================================
// BaseWorkflowService
// ============================================================================

/**
 * Abstract base class for workflow services.
 *
 * Provides a template method pattern for data loading:
 * 1. load() orchestrates the loading process
 * 2. Subclasses define getDataSlots() for what to load
 * 3. Subclasses define transformLoadedData() for domain transformation
 *
 * @example
 * ```typescript
 * class MyWorkflowService extends BaseWorkflowService {
 *     static readonly SERVICE_NAME = 'MyWorkflowService';
 *     readonly WORKFLOW_ID = 'my-workflow';
 *
 *     protected getDataSlots(): string[] {
 *         return ['slot-1', 'slot-2'];
 *     }
 *
 *     protected transformLoadedData(): void {
 *         // Transform raw data to domain objects
 *         const data = this.dataLoader.getVolumeData('slot-1');
 *         // ...
 *     }
 * }
 * ```
 */
export abstract class BaseWorkflowService extends PubSubService implements IWorkflowService {
    /**
     * Workflow identifier - must be implemented by subclasses.
     */
    abstract readonly WORKFLOW_ID: string;

    /**
     * Events emitted by workflow services.
     * Subclasses should extend this with workflow-specific events.
     */
    readonly EVENTS = {
        /** Emitted when loading progress changes */
        LOAD_PROGRESS: 'workflow:load:progress',
        /** Emitted when loading is complete and domain objects are ready */
        LOAD_COMPLETE: 'workflow:load:complete',
        /** Emitted when an error occurs during transformation */
        TRANSFORM_ERROR: 'workflow:transform:error',
        /** Emitted when case metadata is set */
        CASE_METADATA_SET: 'workflow:case-metadata:set',
    } as const;

    /** DataLoader service for fetching raw data */
    protected readonly dataLoader: DataLoaderService;

    /** DisplayFeatureManager for registering display features */
    protected readonly displayManager!: DisplayFeatureManager;

    /** Current loading progress */
    private progress: LoadProgressPayload = {
        loaded: 0,
        total: 0,
        isComplete: false,
    };

    /** Unsubscribe functions for event cleanup */
    private unsubscribers: Array<() => void> = [];

    /** Current case ID being loaded - used to guard against stale events */
    private currentCaseId: string | null = null;

    /** Case metadata stored on the service to avoid React-context refetch cascades */
    private caseMetadata: CaseMetadata | null = null;

    constructor(protected readonly context: ServiceContext) {
        super();
        this.dataLoader = context.services.getService<DataLoaderService>(DataLoaderService.SERVICE_NAME);
        this.displayManager = context.services.getService<DisplayFeatureManager>(
            DisplayFeatureManager.SERVICE_NAME
        );
    }

    /**
     * Load all data for a case.
     * This is the main entry point for loading visualization data.
     *
     * @param caseId - The case ID to load
     */
    async load(caseId: string, signal?: AbortSignal): Promise<void> {
        // Track current case to guard against stale events from concurrent loads
        this.currentCaseId = caseId;

        // Clean up any existing subscriptions
        this.cleanupSubscriptions();

        // Get slots to load from subclass
        const slots = this.getDataSlots();

        // Initialize progress
        this.progress = {
            loaded: 0,
            total: slots.length,
            isComplete: false,
        };

        // Emit initial progress
        this.broadcast(this.EVENTS.LOAD_PROGRESS, { ...this.progress });

        // Capture caseId for closure to guard against stale events from concurrent loads
        const loadCaseId = caseId;

        // Subscribe to slot completion events for progress tracking
        this.unsubscribers.push(
            this.dataLoader.subscribe<SlotLoadedPayload>(this.dataLoader.EVENTS.SLOT_LOADED, () => {
                if (this.currentCaseId === loadCaseId) {
                    this.updateProgress();
                }
            })
        );

        this.unsubscribers.push(
            this.dataLoader.subscribe<{ key: string }>(this.dataLoader.EVENTS.SLOT_ERROR, () => {
                if (this.currentCaseId === loadCaseId) {
                    this.updateProgress();
                }
            })
        );

        this.unsubscribers.push(
            this.dataLoader.subscribe<{ key: string }>(this.dataLoader.EVENTS.SLOT_NOT_FOUND, () => {
                if (this.currentCaseId === loadCaseId) {
                    this.updateProgress();
                }
            })
        );

        // Subscribe to completion event
        this.unsubscribers.push(
            this.dataLoader.subscribe<AllCompletePayload>(this.dataLoader.EVENTS.ALL_COMPLETE, () => {
                if (this.currentCaseId === loadCaseId) {
                    this.handleLoadComplete();
                }
            })
        );

        // Start loading via DataLoaderService
        await this.dataLoader.loadForCase(caseId, this.WORKFLOW_ID, this.getDataSlots(), signal);
    }

    /**
     * Get current loading progress.
     */
    getProgress(): LoadProgressPayload {
        return { ...this.progress };
    }

    /**
     * Get the case metadata stored on this service.
     */
    getCaseMetadata(): CaseMetadata | null {
        return this.caseMetadata;
    }

    /**
     * Set case metadata and broadcast the change.
     */
    setCaseMetadata(metadata: CaseMetadata): void {
        this.caseMetadata = metadata;
        this.broadcast(this.EVENTS.CASE_METADATA_SET, metadata);
    }

    /**
     * Check if the workflow is ready for use.
     * Subclasses may override for workflow-specific readiness checks.
     */
    abstract isReady(): boolean;

    /**
     * Reset the service state.
     */
    override reset(): void {
        // Clear display features before resetting
        this.clearDisplayFeatures();

        this.cleanupSubscriptions();
        this.currentCaseId = null;
        this.caseMetadata = null;
        this.progress = {
            loaded: 0,
            total: 0,
            isComplete: false,
        };
        super.reset();
    }

    // ========================================================================
    // Template Methods - Subclasses must implement
    // ========================================================================

    /**
     * Get the data slot keys needed for this workflow.
     * @returns Array of slot keys to load
     */
    protected abstract getDataSlots(): string[];

    /**
     * Transform loaded raw data into domain objects.
     * Called after all data has finished loading.
     */
    protected abstract transformLoadedData(): void;

    /**
     * Create display features from domain objects.
     * Called after transformLoadedData() completes successfully.
     *
     * Subclasses should register features with DisplayFeatureManager using
     * the naming convention: `{WORKFLOW_ID}-{featureName}`
     *
     * @example
     * ```typescript
     * protected createDisplayFeatures(): void {
     *     const centerlines = this.getCenterlines();
     *     this.displayManager.addFeature({
     *         id: `${this.WORKFLOW_ID}-centerlines`,
     *         type: DisplayFeatureType.CENTERLINE,
     *         data: centerlines,
     *         properties: { tubeDiameter: 0.4; activeTubeDiameter: 0.8 },
     *         visible: true,
     *     });
     * }
     * ```
     */
    protected abstract createDisplayFeatures(): void;

    /**
     * Remove all display features created by this workflow.
     * Uses feature ID prefix convention: `{WORKFLOW_ID}-{featureName}`.
     * Called during reset() to clean up before the next case.
     */
    protected clearDisplayFeatures(): void {
        const prefix = `${this.WORKFLOW_ID}-`;
        for (const feature of this.displayManager.getAllFeatures()) {
            if (feature.id.startsWith(prefix)) {
                this.displayManager.removeFeature(feature.id);
            }
        }
    }

    // ========================================================================
    // Private Methods
    // ========================================================================

    /**
     * Update progress when a slot completes.
     */
    private updateProgress(): void {
        this.progress.loaded++;
        this.broadcast(this.EVENTS.LOAD_PROGRESS, { ...this.progress });
    }

    /**
     * Handle loading completion.
     */
    private handleLoadComplete(): void {
        // Mark loading as complete
        this.progress.isComplete = true;

        // Transform raw data to domain objects
        try {
            this.transformLoadedData();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown transform error';
            console.error('Error transforming loaded data:', error);
            this.broadcast(this.EVENTS.TRANSFORM_ERROR, {
                message,
                error,
            } satisfies TransformErrorPayload);
            return; // Don't proceed if transform failed
        }

        // Create display features from domain objects
        try {
            this.createDisplayFeatures();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown display feature error';
            console.error('Error creating display features:', error);
            this.broadcast(this.EVENTS.TRANSFORM_ERROR, {
                message,
                error,
            } satisfies TransformErrorPayload);
            return; // Don't emit LOAD_COMPLETE after display feature failure
        }

        // Emit completion
        this.broadcast(this.EVENTS.LOAD_COMPLETE, { ...this.progress });
    }

    /**
     * Clean up event subscriptions.
     */
    private cleanupSubscriptions(): void {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
    }
}
